import { Form, Formik, FormikErrors } from 'formik';
import { Theme, css } from '@emotion/react';

import { FormEmailField } from 'components/inputs/FormEmailField';
import { FormPasswordField } from 'components/inputs/FormPasswordField';
import { FormTextField, Grid } from 'ui';
import { SubmitButton, Typography } from 'omakase';
import { consumer } from '@tock/coretypes';
import { getLoyaltyProgramDetails } from 'components/loyalty-program/LoyaltyProgramConstants';
import { isEmail, requireFields } from '@tock/shared/utils/FormValidators';

export interface Fields {
  email: string;
  password: string;
}

interface Props {
  business: consumer.IBusinessConfig;
  isLoading: boolean;
  /**
   * Indicates that the form should prompt for a username rather than an email.
   * Email is preferred and more common, but there are some restaurant groups
   * which have usernames that differ from the guest email address for Paytronix.
   */
  useUsername: boolean;
  onSubmit(values: Fields): Promise<void>;
}

const formWrapper = ({ spacing }: Theme) => css`
  margin: ${spacing(3)}px;
`;

const LoyaltyProgramLoginForm: React.FunctionComponent<Props> = ({
  business,
  isLoading,
  useUsername,
  onSubmit,
}) => {
  const validate = (values: Fields) => {
    const errors: FormikErrors<Fields> = requireFields(values, 'email', 'password');
    if (!useUsername && !isEmail(values.email)) {
      errors.email = 'Please enter a valid email';
    }
    return errors;
  };

  const loyaltyProgramDetails =
    business.loyaltyProgramTheme && getLoyaltyProgramDetails(business.loyaltyProgramTheme);
  return (
    <Formik<Fields>
      initialValues={{ email: '', password: '' }}
      onSubmit={onSubmit}
      validate={validate}
    >
      {({ isSubmitting }) => (
        <Form css={formWrapper}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body02">
                Log in to your {loyaltyProgramDetails?.programName} account to receive points for
                your purchase.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {useUsername ? (
                <FormTextField name="email" label="Username" fullWidth />
              ) : (
                <FormEmailField fullWidth />
              )}
            </Grid>
            <Grid item xs={12}>
              <FormPasswordField fullWidth />
            </Grid>
            <Grid item xs={12}>
              <SubmitButton
                variant="primary"
                data-testid="signin"
                disabled={isLoading || isSubmitting}
                isSubmitting={isLoading || isSubmitting}
                fullWidth
                size="large"
              >
                Next
              </SubmitButton>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default LoyaltyProgramLoginForm;
